import { useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useOrgScope, type OrgOption } from '@/stores/orgScope';

/**
 * Admin org-switcher — one consistent dropdown for org-scoped screens so an admin
 * can move between organizations. Renders nothing for non-admins (always scoped to
 * their own org).
 *
 * Two modes:
 *  - self-contained (default): backed by the shared `useOrgScope` store, fetches the
 *    org list itself, and defaults to the admin's own org. Use this on new screens.
 *  - controlled: pass `value` + `onChange` (+ `orgs`) to drive it from a screen that
 *    already owns its org state (e.g. Manager), keeping that screen's logic intact
 *    while still rendering the identical control.
 */
export function OrgSwitcher({
  className = '',
  value,
  onChange,
  orgs: orgsProp,
}: {
  className?: string;
  value?: string;
  onChange?: (id: string) => void;
  orgs?: OrgOption[];
}) {
  const role = useAuthStore((s) => s.user?.role);
  const ownOrgId = useAuthStore((s) => s.user?.orgId);
  const storeSelected = useOrgScope((s) => s.selectedOrgId);
  const storeOrgs = useOrgScope((s) => s.orgs);
  const ensureOrgs = useOrgScope((s) => s.ensureOrgs);
  const storeSet = useOrgScope((s) => s.setSelectedOrgId);

  const controlled = onChange !== undefined;
  const selectedOrgId = controlled ? value ?? '' : storeSelected;
  const orgs = orgsProp ?? storeOrgs;
  const setSelectedOrgId = controlled ? onChange! : storeSet;

  useEffect(() => {
    // In controlled mode the parent owns the org list + default; don't fight it.
    if (role !== 'admin' || controlled) return;
    ensureOrgs();
    if (!selectedOrgId && ownOrgId) storeSet(ownOrgId);
  }, [role, controlled, ownOrgId, selectedOrgId, ensureOrgs, storeSet]);

  if (role !== 'admin') return null;

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <Building2 size={14} className="text-muted-foreground" />
      <select
        value={selectedOrgId}
        onChange={(e) => setSelectedOrgId(e.target.value)}
        title="Viewing organization"
        className="rounded-md border border-border bg-card px-2 py-1 text-xs text-foreground focus:border-primary focus:outline-none"
      >
        {orgs.length === 0 && <option value={selectedOrgId}>{selectedOrgId ? 'current org' : 'loading…'}</option>}
        {orgs.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}
